import React from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  MEMORIAL_KEEPSAKES_PAID_BADGE,
  MEMORIAL_PHOTO_LINK_CHECKOUT_PUBLISHED,
  MEMORIAL_PHOTO_LINK_CHECKOUT_UNPUBLISHED,
  MEMORIAL_PHOTO_LINK_ORDERING_PAID_ONLY,
  MEMORIAL_PHOTO_LINK_PRICE_PLACEHOLDER,
  MEMORIAL_PHOTO_LINK_REQUEST_NOTE,
  MEMORIAL_PHOTO_LINK_REQUEST_SAVED,
  MEMORIAL_PHOTO_LINK_REQUEST_TITLE,
} from '../../content/memorialKeepsakesContent';
import { getOrientationLabel } from '../../utils/memorialPhotoLinkBuilder';

export function MemorialPhotoLinkCheckoutReviewDialog({
  open,
  onOpenChange,
  product,
  frameStyle,
  imageStatus,
  orientation,
  published,
  personName,
  memorialTitle,
  paidMember,
  saving = false,
  onSave,
  onBackToEdit,
}) {
  const qrStatus = published ? 'Live' : 'Activation pending';
  const certificateStatus = published ? 'Ready when ordered' : 'Activation pending';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-md overflow-y-auto border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] sm:max-w-lg"
        data-testid="memorial-photo-link-checkout-review"
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="rounded-full text-[10px] uppercase">
              {MEMORIAL_KEEPSAKES_PAID_BADGE}
            </Badge>
          </div>
          <DialogTitle className="font-serif text-xl font-light">{MEMORIAL_PHOTO_LINK_REQUEST_TITLE}</DialogTitle>
          <DialogDescription className="text-sm text-[#5c534a]">{MEMORIAL_PHOTO_LINK_REQUEST_NOTE}</DialogDescription>
        </DialogHeader>

        <p className="rounded-lg border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)] px-3 py-2 text-sm text-[#5c534a]">
          {published ? MEMORIAL_PHOTO_LINK_CHECKOUT_PUBLISHED : MEMORIAL_PHOTO_LINK_CHECKOUT_UNPUBLISHED}
        </p>

        <dl className="space-y-3 rounded-xl border border-[hsl(38_14%_86%)] bg-white/80 p-4 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Person</dt>
            <dd className="text-right font-medium text-[#1c1917]">{personName || '—'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Memorial title</dt>
            <dd className="max-w-[60%] text-right text-[#1c1917]">{memorialTitle || '—'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Size</dt>
            <dd className="text-right font-medium text-[#1c1917]">{product?.title || '—'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Orientation</dt>
            <dd className="text-right text-[#1c1917]">{getOrientationLabel(orientation)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Frame colour</dt>
            <dd className="text-right text-[#1c1917]">{frameStyle?.label || '—'}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Image</dt>
            <dd className="text-right text-[#1c1917]">{imageStatus || '—'}</dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-[hsl(38_14%_88%)] pt-3">
            <dt className="text-muted-foreground">QR/NFC status</dt>
            <dd className="text-right font-medium text-[#1c1917]">{qrStatus}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Legacy Link Certificate</dt>
            <dd className="text-right text-[#1c1917]">{certificateStatus}</dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-[hsl(38_14%_88%)] pt-3">
            <dt className="text-muted-foreground">Price</dt>
            <dd className="text-right font-medium text-[#1c1917]">{MEMORIAL_PHOTO_LINK_PRICE_PLACEHOLDER}</dd>
          </div>
        </dl>

        {!paidMember ? (
          <div className="rounded-lg border border-[hsl(38_14%_86%)] bg-white/70 px-3 py-2 text-sm text-[#5c534a]">
            <p>{MEMORIAL_PHOTO_LINK_ORDERING_PAID_ONLY}</p>
            <Button type="button" size="sm" variant="outline" className="mt-2 rounded-full" asChild>
              <Link to="/pricing">View plans</Link>
            </Button>
          </div>
        ) : null}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button type="button" variant="ghost" className="rounded-full" onClick={onBackToEdit}>
            Back to edit
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-full bg-[hsl(152_32%_20%)] text-[hsl(42_30%_96%)] hover:bg-[hsl(152_28%_24%)]"
              disabled={!paidMember || saving}
              onClick={onSave}
            >
              {saving ? 'Saving…' : 'Save keepsake request'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** @deprecated use MemorialPhotoLinkCheckoutReviewDialog */
export const MemorialPhotoLinkRequestDialog = MemorialPhotoLinkCheckoutReviewDialog;

export { MEMORIAL_PHOTO_LINK_REQUEST_SAVED };

export default MemorialPhotoLinkCheckoutReviewDialog;
